/**
 * The one place a flat comment list becomes a thread.
 *
 * THE ROUTE RETURNS THEM FLAT AND CHRONOLOGICAL, nesting and all. Verified against
 * production rather than assumed: post 352388 has 38 comments where a reply written
 * at 23:44 sits between the top-level comments of 23:41 and 23:51, with only its
 * `parent_id` saying it belongs under the first of them. So chronological order and
 * READING order are different things, and anything that wants the second has to
 * build the tree.
 *
 * TWO CALLERS NEEDED THE SAME ANSWER and only one of them had it. `CommentThread`
 * built the tree to render it; the inbox sorted the rows it had to answer by
 * `created_at` and called that top-to-bottom. It is not: a nested reply is displayed
 * under its parent and sorted by the clock, so the reply box could jump from halfway
 * down the thread back up to the middle of it. One builder now, so "the order they
 * are shown in" has a single definition.
 */

export interface Threadable {
  id: number;
  parent_id?: number | null;
  pinned?: boolean | null;
}

export type TreeNode<T> = T & { children: TreeNode<T>[] };

/**
 * Roots, each carrying its replies.
 *
 * Replies keep the order they arrived in — chronological, which is what a
 * conversation is. Roots are the same, except that PINNED ones float to the top,
 * because that is what pinning means and it is a stable sort so nothing else moves.
 * A comment whose parent is not in the list becomes a root rather than vanishing.
 */
export function buildThreadTree<T extends Threadable>(comments: T[]): TreeNode<T>[] {
  const byId = new Map<number, TreeNode<T>>();
  for (const c of comments) byId.set(c.id, { ...c, children: [] });
  const roots: TreeNode<T>[] = [];
  for (const c of comments) {
    const node = byId.get(c.id)!;
    const parent = c.parent_id != null ? byId.get(c.parent_id) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots.sort((a, b) => Number(b.pinned ?? false) - Number(a.pinned ?? false));
}

/**
 * The comments in the order they are READ — the exact order `CommentThread` renders
 * them: each root, then everything nested beneath it, then the next root. This is what
 * "top to bottom" means for anything walking a thread.
 *
 * Not exported: `threadRank` is what callers actually want, and a second way to ask the
 * same question is a second thing to keep in step.
 */
function threadOrder<T extends Threadable>(comments: T[]): T[] {
  const out: T[] = [];
  const walk = (nodes: TreeNode<T>[]): void => {
    for (const n of nodes) {
      const { children, ...rest } = n;
      out.push(rest as unknown as T);
      walk(children);
    }
  };
  walk(buildThreadTree(comments));
  return out;
}

/** Where each comment sits top-to-bottom, by id. For sorting a subset of a thread
 *  — the handful of rows an inbox is asking about — into reading order. */
export function threadRank(comments: Threadable[]): Map<number, number> {
  return new Map(threadOrder(comments).map((c, i) => [c.id, i]));
}
